import {
  completionTimeBuffer,
  getDateTime,
} from '@pinnacleautomotive/clarity-components';
import { createSlice } from '@reduxjs/toolkit';
import {
  setServiceOrderSelectedVendor,
  getServiceOrderServicesByDepartment,
  addPriceToSelectedServiceOrderPrices,
  removePriceToSelectedServiceOrderPrices,
  setServiceOrderServicesComplianceTime,
  getServiceOrderVehicleByVin,
  saveServiceOrder,
  clearServiceOrder,
  editServiceOrderVehicleDetails,
  addServiceOrderVehicleDetails,
  openServiceOrderEditVehicleModal,
  closeServiceOrderEditVehicleModal,
  setServiceOrderObservations,
  addServiceOrderSpecialRequest,
  removeServiceOrderSpecialRequest,
  changeServiceOrderCompletionType,
  setServiceOrderPromiseDate,
  updateServiceOrderCreatedDate,
  discardServiceOrder,
  clearServiceOrderError,
  setServiceOrderAlternativeOwner,
  serviceOrderAlternativeOwner,
  setServiceOrderSelectedServicesPrices,
  setAllServiceOrderDealers,
  setVehicleStockNumber,
} from '../../actions/orders/newServiceOrderActions';
import {
  initialState,
  isLoadingReducer,
  closeEditVehicleModalReducer,
  vehicleDetailFulfilled,
  vehicleDetailPending,
  vehicleDetailRejected,
  saveLoadingReducer,
} from './newOrderReducer';

const initialServiceOrderState = {
  ...initialState,
  selectedServicesPrices: [],
};

const newServiceOrder = createSlice({
  name: 'newServiceOrder',
  initialState: initialServiceOrderState,
  extraReducers: {
    [saveServiceOrder.pending]: saveLoadingReducer,
    [getServiceOrderVehicleByVin.pending]: isLoadingReducer('isLoadingVehicle'),
    [editServiceOrderVehicleDetails.pending]: vehicleDetailPending,
    [addServiceOrderVehicleDetails.pending]: vehicleDetailPending,
    [closeServiceOrderEditVehicleModal.type]: closeEditVehicleModalReducer,
    [editServiceOrderVehicleDetails.fulfilled]: vehicleDetailFulfilled,
    [addServiceOrderVehicleDetails.fulfilled]: vehicleDetailFulfilled,
    [addServiceOrderVehicleDetails.rejected]: vehicleDetailRejected,
    [editServiceOrderVehicleDetails.rejected]: vehicleDetailRejected,
    [setServiceOrderObservations.type]: (state, { payload }) => {
      state.observation = payload;
    },
    [setServiceOrderAlternativeOwner.type]: (state, { payload }) => {
      state.alternativeOwner = payload;
    },
    [addServiceOrderSpecialRequest.type]: (state, { payload }) => {
      state.specialRequests.push(payload);
    },
    [removeServiceOrderSpecialRequest.type]: (state, { payload }) => {
      state.specialRequests.splice(payload, 1);
    },
    [setServiceOrderSelectedVendor.type]: (state, { payload }) => {
      if (state.selectedVendor?.companyId === payload.companyId) {
        state.selectedVendor = null;
        state.alternativeOwner = { label: '', personId: null };
        state.owners = [];
      } else state.selectedVendor = payload;

      state.selectedDepartment = '';
      state.selectedPrices = [];
      state.prices = [];
      state.categories = [];
      state.selectedCategories = [];
    },
    [openServiceOrderEditVehicleModal.type]: state => {
      state.editVehicleModalOpen = true;
    },
    [setServiceOrderPromiseDate.type]: (state, { payload }) => {
      state.promiseDate = payload;
    },
    [getServiceOrderServicesByDepartment.pending]: (
      state,
      { meta: { arg } }
    ) => {
      state.isLoading = true;
      state.isLoadingServices = true;
      state.selectedDepartment = arg.department;
      state.prices = [];
      state.selectedPrices = [];
      state.categories = [];
      state.selectedCategories = [];
    },
    [getServiceOrderServicesByDepartment.fulfilled]: (state, { payload }) => {
      state.isLoading = false;
      state.isLoadingServices = false;
      state.prices = payload.prices;
      state.completionTime = 0;
      calculatePromiseDate(state);
    },
    [addPriceToSelectedServiceOrderPrices.type]: (state, { payload }) => {
      state.selectedPrices.push(payload.servicePriceId);
      state.categories = [...state.categories, payload.service.type];
      state.selectedCategories = [
        ...new Set([...state.categories, payload.service.type]),
      ];
      state.servicesTotalComplianceTime += payload.localComplianceTime;
      calculatePromiseDate(state);
    },
    [removePriceToSelectedServiceOrderPrices.type]: (state, { payload }) => {
      state.selectedPrices = state.selectedPrices.filter(
        price => price !== payload.servicePriceId
      );
      state.categories = state.categories.filter(
        (category, index) =>
          category !== payload.service.type ||
          state.categories.indexOf(category) !== index
      );
      state.selectedCategories = [...new Set(state.categories)];
      state.servicesTotalComplianceTime -= payload.localComplianceTime;
      calculatePromiseDate(state);
    },
    [setServiceOrderServicesComplianceTime.type]: (state, { payload }) => {
      state.servicesTotalComplianceTime += payload;
      calculatePromiseDate(state);
    },
    [setAllServiceOrderDealers.pending]: state => {
      state.isLoading = true;
      state.dealers = [];
    },
    [setAllServiceOrderDealers.fulfilled]: (state, { payload }) => {
      state.isLoading = false;
      state.dealers = payload.dealers;
    },
    [setAllServiceOrderDealers.rejected]: state => {
      state.isLoading = false;
      state.dealers = [];
    },
    [setServiceOrderSelectedServicesPrices.type]: (state, { payload }) => {
      state.selectedServicesPrices = payload;
    },
    [getServiceOrderVehicleByVin.fulfilled]: (state, { payload }) => {
      state.selectedVehicle = payload;
      state.vehicleNotFound = false;
      state.isLoading = false;
      state.isLoadingVehicle = false;
      state.po = null;
      state.stockNumber = payload?.custody?.stockNumber;
      state.vehicleId = payload?.vehicleId;
      state.ro = null;
    },
    [getServiceOrderVehicleByVin.rejected]: state => {
      state.isLoading = false;
      state.isLoadingVehicle = false;
      state.selectedVehicle = null;
      state.vehicleNotFound = true;
    },
    [setVehicleStockNumber.type]: (state, { payload }) => {
      state.stockNumber = payload;
    },
    [saveServiceOrder.fulfilled]: (state, { payload }) => {
      state.isLoading = false;
      state.workOrderSaved = payload;
      state.saveError = null;
    },
    [saveServiceOrder.rejected]: (state, { payload }) => {
      state.isLoading = false;
      state.workOrderSaved = null;
      state.saveError = { token: 'UNEXPECTED_ERROR', ...payload };
    },
    [changeServiceOrderCompletionType.type]: (state, { payload }) => {
      state.completionType = payload;
    },
    [updateServiceOrderCreatedDate.type]: state => {
      state.createdDate = new Date().getTime();
      calculatePromiseDate(state);
    },
    [clearServiceOrderError.type]: state => {
      state.saveError = null;
      state.editVehicleDataError = false;
    },
    [clearServiceOrder.type]: () => ({ ...initialState }),
    [discardServiceOrder.type]: () => ({ ...initialState, discarded: true }),
    [serviceOrderAlternativeOwner.pending]: state => {
      state.alternativeOwnerLoading = true;
      state.owners = null;
    },
    [serviceOrderAlternativeOwner.fulfilled]: (state, { payload }) => {
      state.alternativeOwnerLoading = false;
      state.owners = payload.users;
    },
    [serviceOrderAlternativeOwner.rejected]: state => {
      state.alternativeOwnerLoading = false;
    },
  },
});

function calculatePromiseDate(state) {
  state.promiseDate = getDateTime(new Date(state.createdDate))
    .plus({ minutes: state.servicesTotalComplianceTime })
    .plus({ minutes: completionTimeBuffer * 2 })
    .toJSDate()
    .getTime();
}

export default newServiceOrder;
